import React, { useEffect, useState } from "react";
import {
  getRolesApi,
  getBuilderUsersApi,
  createBuilderUserApi,
  updateBuilderUserApi,
  deleteBuilderUserApi
} from "../../services/apis/builderUserApis";
import { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import UserRole from "./UserRole";
import UserModal from "./UserModal";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import UserAssignOrgApp from "./UserAssignOrgApp";
import BuilderUsersTable from "./BuilderUsersTable";
import "./User.css";
import { useParams } from "react-router-dom";
import Loader from "../../Layouts/Loader/Loader";
import Input from "../../components/FormComponents/Input";
import Pagination from "./Pagination";
import Button from "../../components/FormComponents/Button";
import SyncCongnitoUser from "./SyncCongnitoUser";
import { encryptData } from "../Login/Login";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import storageService from "../../services/storageService";
import { STORAGE_KEYS } from "../../constant/constant";

const BuilderUsersSection = () => {
  const initialLimit = Number(storageService.get(STORAGE_KEYS.LIMIT) || 10);

  const [filters, setFilters] = useState({ page: 1, limit: initialLimit, search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [pageTrack, setPageTrack] = useState({ currPage: 1, totalPages: 1 });
  const [isLoader, setIsLoader] = useState(false);

  const [allUsersData, setAllUsersData] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [modal, setModal] = useState({ isOpen: false, type: null, data: null });

  const params = useParams();
  const { getUserConfirmationForDelete } = useModalContext();

  const closeModal = () => setModal({ isOpen: false, type: null, data: null });
  const openAddUser = () => setModal({ isOpen: true, type: "ADD", data: null });
  const openEditUser = (user) => setModal({ isOpen: true, type: "EDIT", data: user });
  const openAssign = (user) => setModal({ isOpen: true, type: "ASSIGN", data: user });

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // --- Data Fetching ---
  useEffect(() => {
    const controller = new AbortController();
    const fetchAllUsers = async () => {
      setIsLoader(true);
      try {
        const { search, page, limit } = filters;

        const response = await getBuilderUsersApi({
          search,
          page,
          limit,
          signal: controller.signal
        });

        setPageTrack({
          currPage: response.data.current_page,
          totalPages: response.data.total_pages
        });

        const formattedData = response.data.users?.map((user) => ({
          ...user,
          first_name: user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1),
          last_name: user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1)
        }));

        setAllUsersData(formattedData || []);
      } catch (error) {
        if (error.name !== "CanceledError") handleApiError(error);
      } finally {
        if (!controller.signal.aborted) setIsLoader(false);
      }
    };
    fetchAllUsers();
    return () => controller.abort();
  }, [filters]);

  useEffect(() => {
    const fetchAllRoles = async () => {
      try {
        const response = await getRolesApi();
        setAllRoles(response.data);
      } catch (error) {
        handleApiError(error);
      }
    };
    fetchAllRoles();
  }, []);

  const handleSearchInputChange = (e) => setSearchInput(e.target.value);
  const onPageChangeAPI = (pageNumber) => setFilters((prev) => ({ ...prev, page: pageNumber }));
  const onLimitChangeAPI = (newLimit) => {
    storageService.set(STORAGE_KEYS.LIMIT, newLimit);
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSaveUser = async (formData, selectedRoles) => {
    try {
      const roleIds = selectedRoles.map((role) => role.id);

      if (modal.type === "ADD") {
        const payload = {
          ...formData,
          roles: roleIds,
          email: encryptData(formData.email),
          password: encryptData(formData.password)
        };

        const response = await createBuilderUserApi(payload);

        setAllUsersData((prev) => [...prev, response.data]);
        toastSuccess(response.data.message || "User created successfully");
      } else if (modal.type === "EDIT") {
        const payload = { ...formData, roles: roleIds };
        delete payload["email"];
        if (!payload.password?.trim()) delete payload["password"];

        const response = await updateBuilderUserApi(modal.data.id, payload);

        setAllUsersData((prev) => prev.map((u) => (u.id === modal.data.id ? { ...u, ...payload } : u)));
        toastSuccess(response.data.message || "User updated successfully");
      }
      closeModal();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAssignUserToOrgApp = async (userId, selectedOrgs, selectedApps) => {
    try {
      const newOrgArray = selectedOrgs.map((item) => item.id.toString());
      const newAppArray = selectedApps.map((item) => item.id.toString());
      const payload = { ...modal.data, apps: newAppArray, orgs: newOrgArray };

      const response = await updateBuilderUserApi(userId, payload);

      setAllUsersData((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, orgs: selectedOrgs, apps: selectedApps } : user))
      );
      closeModal();
      toastSuccess(response.data.message || "Assignment updated successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteUser = async (id, firstName, lastName) => {
    try {
      if (!(await getUserConfirmationForDelete(firstName + " " + lastName))) return;

      const response = await deleteBuilderUserApi(id);

      setAllUsersData(allUsersData.filter((user) => user.id !== id));
      toastSuccess(response.data.message || "User deleted successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const getModalTitle = () => {
    if (modal.type === "ASSIGN") return `${modal.data.first_name}`;
    if (modal.type === "EDIT") return "Edit User";
    return "Add User";
  };

  return (
    <div className="application_container">
      <AppLeftSideBar type={params.orgId ? "app" : "org"} />
      <div className="dashboard_outer_section">
        <div className="dashboard-top-header">
          <h2>Builder Users</h2>
          <div className="d_flex justify_between mr_0 mx_0 gap_3">
            <div className="position_relative">
              <Input
                autoFocus
                placeholder="Search User"
                value={searchInput}
                onChange={handleSearchInputChange}
                containerStyle={{ position: "relative", width: "100%" }}
                inputBoxStyle={{ paddingLeft: "28px", height: "36px" }}
              />
              <MaterialIcon
                name="search"
                className="search position_absolute font_size_large"
                style={{ top: "8px", left: "8px" }}
              />
            </div>
            <div className="d_flex gap_3">
              <Button onClick={openAddUser}>Add User</Button>
              <UserRole />
              <SyncCongnitoUser path="/user-services/users/cognito/builder" />
            </div>
          </div>
        </div>

        {isLoader ? (
          <Loader width={70} />
        ) : (
          <div className="org-dashboard-page-body">
            <BuilderUsersTable
              data={allUsersData}
              allRoles={allRoles}
              onEdit={openEditUser}
              onDelete={deleteUser}
              onAssign={openAssign}
            />
            <div className="p_3 bottom_border">
              <Pagination
                pageTrack={pageTrack}
                pagination={filters}
                onPageChange={onPageChangeAPI}
                onLimitChange={onLimitChangeAPI}
              />
            </div>
          </div>
        )}

        <BuilderModal isOpen={modal.isOpen} onClose={closeModal} modalName={getModalTitle()}>
          {modal.type === "ASSIGN" ? (
            <div className="w_50_vw">
              <UserAssignOrgApp userId={modal.data.id} onAssign={handleAssignUserToOrgApp} />
            </div>
          ) : (
            <UserModal initialData={modal.data} allRoles={allRoles} onSave={handleSaveUser} />
          )}
        </BuilderModal>
      </div>
    </div>
  );
};

export default BuilderUsersSection;
